import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DPANav from '@/components/ui/dpa-nav';
import Breadcrumbs from '@/components/ui/breadcrumbs';
import { getTermsSections } from '@/lib/terms-data';
import { useScrollHandler } from '@/utils/scrollUtils';

interface TermsSection {
  id: string;
  title: string;
  content: string;
  order: number;
}

interface TermsData {
  title: string;
  sections: TermsSection[];
  totalCount: number;
  generatedAt: string;
}

const DPA: React.FC = () => {
  const [sections, setSections] = useState<TermsSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [title, setTitle] = useState<string>('Data Processing Agreement');
  const location = useLocation();
  const navigate = useNavigate();

  // Use our scroll library
  useScrollHandler(location.hash.slice(1));

  useEffect(() => {
    const loadSections = async () => {
      try {
        const termsData = await getTermsSections('dpa');
        setSections(termsData.sections);
        setTitle(termsData.title);
        if (termsData.sections.length > 0) {
          setLastUpdated(termsData.generatedAt);
        }
      } catch (err) {
        setError('Failed to load DPA content');
        console.error('Error loading DPA:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSections();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen font-sans">
        <Navbar />
        <main className="py-32 bg-gray-50" role="main">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="animate-pulse space-y-8" role="status" aria-label="Loading DPA content">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen font-sans">
        <Navbar />
        <main className="py-32 bg-gray-50" role="main">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="text-red-600" role="alert">{error}</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = "Data Processing Agreement - Strive | AI-Powered Product Operations Platform";
  const pageDescription = "Read Strive's Data Processing Agreement (DPA) to understand how we handle and protect your data.";
  const canonicalUrl = "https://strive.us/dpa";

  return (
    <div className="min-h-screen font-sans">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="data processing agreement, DPA, data protection, privacy, GDPR, data handling, legal agreement" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Strive" />
        <meta property="article:modified_time" content={lastUpdated} />
        <meta property="article:section" content="Legal" />
        <meta property="article:tag" content="data processing agreement,privacy,data protection" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Additional SEO tags */}
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="language" content="English" />
        <meta name="author" content="Strive" />
        <meta name="revisit-after" content="7 days" />
        <meta name="last-modified" content={lastUpdated} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TermsAndConditions",
            "name": "Strive Data Processing Agreement",
            "version": "1.0",
            "dateModified": lastUpdated,
            "publisher": {
              "@type": "Organization",
              "name": "Strive",
              "logo": {
                "@type": "ImageObject",
                "url": "https://strive.us/White_Logomark.svg",
                "width": "201",
                "height": "54"
              }
            },
            "isPartOf": {
              "@type": "WebSite",
              "name": "Strive",
              "url": "https://strive.us"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://strive.us"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Data Processing Agreement",
                  "item": canonicalUrl
                }
              ]
            },
            "mainEntity": {
              "@type": "Article",
              "headline": "Data Processing Agreement",
              "description": pageDescription,
              "dateModified": lastUpdated,
              "articleSection": "Legal",
              "keywords": "data processing agreement, privacy, data protection",
              "author": {
                "@type": "Organization",
                "name": "Strive"
              }
            }
          })}
        </script>
      </Helmet>

      <Navbar />
      
      <main 
        className="py-32 bg-gray-50" 
        role="main" 
        aria-labelledby="dpa-title"
        tabIndex={-1}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Breadcrumbs 
            items={[
              { name: 'Data Processing Agreement' }
            ]} 
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-12">
            <aside 
              role="complementary" 
              aria-label="DPA navigation"
              className="sticky top-24"
            >
              <DPANav />
            </aside>
            
            <article 
              className="max-w-4xl"
              aria-labelledby="dpa-title"
            >
              <header>
                <h1 
                  id="dpa-title" 
                  className="text-4xl font-bold text-gray-900 mb-4"
                >
                  {title}
                </h1>
                {lastUpdated && (
                  <p className="text-gray-600 mb-12">
                    <time 
                      dateTime={lastUpdated}
                      aria-label={`Last updated: ${new Date(lastUpdated).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}`}
                    >
                      Last updated: {new Date(lastUpdated).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </time>
                  </p>
                )}
              </header>

              <div 
                className="prose prose-lg max-w-none"
                role="region"
                aria-label="DPA content"
              >
                {sections.map((section) => (
                  <section 
                    key={section.id} 
                    id={section.id} 
                    className="scroll-mt-20 mb-12"
                    aria-labelledby={`section-${section.id}-title`}
                  >
                    <h2 
                      id={`section-${section.id}-title`}
                      className="text-2xl font-semibold text-gray-900 mb-4"
                    >
                      {section.title}
                    </h2>
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <p className="text-gray-700 mb-4 leading-relaxed">{children}</p>
                        ),
                        ul: ({ children }) => (
                          <ul 
                            className="list-disc pl-6 mb-4 space-y-2" 
                            role="list"
                          >
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol 
                            className="list-decimal pl-6 mb-4 space-y-2" 
                            role="list"
                          >
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li className="text-gray-700">{children}</li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-gray-900">{children}</strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic text-gray-700">{children}</em>
                        ),
                        a: ({ href, children }) => (
                          <a 
                            href={href} 
                            className="text-primary-600 hover:text-primary-700 underline"
                            target={href?.startsWith('http') ? '_blank' : undefined}
                            rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                            aria-label={`${children}${href?.startsWith('http') ? ' (opens in new tab)' : ''}`}
                          >
                            {children}
                          </a>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote 
                            className="border-l-4 border-gray-200 pl-4 italic text-gray-700 my-4"
                            role="note"
                          >
                            {children}
                          </blockquote>
                        ),
                        code: ({ children }) => (
                          <code 
                            className="bg-gray-100 rounded px-1 py-0.5 text-sm font-mono"
                            role="text"
                          >
                            {children}
                          </code>
                        ),
                        pre: ({ children }) => (
                          <pre 
                            className="bg-gray-100 rounded p-4 overflow-x-auto my-4"
                            role="text"
                          >
                            {children}
                          </pre>
                        ),
                        br: () => <br className="my-2" />,
                        hr: () => <hr className="my-8 border-gray-200" />,
                      }}
                    >
                      {section.content}
                    </ReactMarkdown>
                  </section>
                ))}
              </div>
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DPA; 